"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTableFilters = void 0;
const common_types_1 = require("../../../common-types");
const user_settings_context_1 = require("../../../settings/user/user-settings-context");
const react_1 = require("react");
const event_callback_hook_1 = require("../../../utils/event-callback-hook");
const update_effect_1 = require("../../../utils/update-effect");
const boolean_cell_1 = require("../cells/boolean-cell");
const date_cell_1 = require("../cells/date-cell");
const date_time_cell_1 = require("../cells/date-time-cell");
const number_cell_1 = require("../cells/number-cell");
const text_cell_1 = require("../cells/text-cell");
const time_cell_1 = require("../cells/time-cell");
const table_filter_cells_1 = require("../table-filter-cells");
function useTableFilters({ tableId, version, columns, initPreFilters, }) {
    /**
     * Filters definition.
     */
    const filters = (0, react_1.useMemo)(() => deriveFilters(columns), [columns]);
    const { getTableSettings, setTableSettings } = (0, react_1.useContext)(user_settings_context_1.UserSettingsContext);
    let settings;
    let initFiltersState = deriveFiltersState(filters);
    if (tableId !== '') {
        settings = getTableSettings(tableId, version);
        if ((settings === null || settings === void 0 ? void 0 : settings.filtersState) !== undefined) {
            initFiltersState = settings === null || settings === void 0 ? void 0 : settings.filtersState;
        }
    }
    /**
     * Filters state.
     */
    const [filtersState, setFiltersState] = (0, react_1.useState)(initFiltersState);
    /**
     * Prefilter state.
     */
    const [preFilters, setPreFilters] = (0, react_1.useState)(initPreFilters);
    /**
     * Advanced filters
     */
    const [advancedFilters, setAdvancedFilters] = (0, react_1.useState)([]);
    /**
     * Passed to api to highlight search results.
     */
    const [highlights, setHighlights] = (0, react_1.useState)([]);
    const filterDialogRef = (0, react_1.useRef)(null);
    const openFilterDialog = (0, event_callback_hook_1.useEventCallback)(() => { var _a; return (_a = filterDialogRef.current) === null || _a === void 0 ? void 0 : _a.open(); });
    const closeFilterDialog = (0, event_callback_hook_1.useEventCallback)(() => { var _a; return (_a = filterDialogRef.current) === null || _a === void 0 ? void 0 : _a.close(); });
    /**
     * Updates user settings.
     */
    (0, update_effect_1.useUpdateEffect)(() => {
        const newSettings = {
            ...(settings !== null && settings !== void 0 ? settings : {}),
            filtersState,
            version,
        };
        setTableSettings(tableId, newSettings);
    }, [filtersState]);
    return {
        filterDialogRef,
        openFilterDialog,
        closeFilterDialog,
        filters,
        filtersState,
        setFiltersState,
        preFilters,
        setPreFilters,
        advancedFilters,
        setAdvancedFilters,
        highlights,
        setHighlights,
    };
}
exports.useTableFilters = useTableFilters;
/**
 * Derives default filters from supplied colums.
 */
function deriveFilters(columns) {
    return columns
        .filter(({ filterable = false }) => filterable)
        .map(({ name, datakey, filterkey, CellComponent, FilterComponent, filterOperation, filterPath, filterGroup, filterOrder, asciiFolding, }) => ({
        enabled: false,
        label: name,
        operation: filterOperation !== null && filterOperation !== void 0 ? filterOperation : deriveOperation(CellComponent),
        filterPath: filterPath !== null && filterPath !== void 0 ? filterPath : undefined,
        datakey,
        filterkey: filterkey || datakey,
        FilterComponent: FilterComponent !== null && FilterComponent !== void 0 ? FilterComponent : deriveComponent(CellComponent),
        value: '',
        filterGroup: filterGroup !== null && filterGroup !== void 0 ? filterGroup : 0,
        filterOrder: filterOrder !== null && filterOrder !== void 0 ? filterOrder : 0,
        asciiFolding,
    }));
}
function deriveOperation(CellComponent) {
    switch (CellComponent) {
        case boolean_cell_1.BooleanCell:
            return common_types_1.ApiFilterOperation.EQ;
        case number_cell_1.NumberCell:
            return common_types_1.ApiFilterOperation.AND;
        case text_cell_1.TextCell:
            return common_types_1.ApiFilterOperation.CONTAINS;
        case date_cell_1.DateCell:
            return common_types_1.ApiFilterOperation.AND;
        case date_time_cell_1.DateTimeCell:
            return common_types_1.ApiFilterOperation.AND;
        default:
            return common_types_1.ApiFilterOperation.NOT_NULL;
    }
}
function deriveComponent(CellComponent) {
    switch (CellComponent) {
        case boolean_cell_1.BooleanCell:
            return table_filter_cells_1.TableFilterCells.FilterBooleanCell;
        case date_cell_1.DateCell:
            return table_filter_cells_1.TableFilterCells.FilterDateCell;
        case date_time_cell_1.DateTimeCell:
            return table_filter_cells_1.TableFilterCells.FilterDateTimeCell;
        case time_cell_1.TimeCell:
            return table_filter_cells_1.TableFilterCells.FilterTimeCell;
        case number_cell_1.NumberCell:
            return table_filter_cells_1.TableFilterCells.FilterNumberCell;
        case text_cell_1.TextCell:
        default:
            return table_filter_cells_1.TableFilterCells.FilterTextCell;
    }
}
/**
 * Derives filter states from supplied/constructed filters.
 */
function deriveFiltersState(filters) {
    return filters.map((filter) => ({
        enabled: false,
        value: null,
        operation: filter.operation,
    }));
}
//# sourceMappingURL=table-filters-hook.js.map